import { ThirdPartyRefundProvider } from "@redotech/congo-enums/third-party-refund-provider";
import { ReturnDisbursementStatus } from "@redotech/crdb-enums/returns/return-disbursement-status";
import { useTranslation } from "@redotech/redo-i18n";
import BankIcon from "@redotech/redo-icons/bank.svg";
import PaypalIcon from "@redotech/redo-icons/paypal-color.svg";
import VenmoIcon from "@redotech/redo-icons/venmo-color.svg";
import {
  CustomerRefundProvider,
  thirdPartyRefundProviderName,
} from "@redotech/redo-return-core-common/third-party-refunds/recipient";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

export interface RefundMethodSummaryProps {
  provider: CustomerRefundProvider;
  /** The wallet address the payout goes to; empty for a bank account, which has none to show. */
  recipient: string;
  status: ReturnDisbursementStatus;
  canEdit: boolean;
  onEditClick: () => void;
}

export const RefundMethodSummary = memo(function RefundMethodSummary({
  provider,
  recipient,
  status,
  canEdit,
  onEditClick,
}: RefundMethodSummaryProps) {
  const { t } = useTranslation();

  const providerCopy: Record<
    CustomerRefundProvider,
    { icon: React.ReactNode; label: string }
  > = {
    [ThirdPartyRefundProvider.Paypal]: {
      icon: <PaypalIcon height={24} width={24} />,
      label: thirdPartyRefundProviderName(ThirdPartyRefundProvider.Paypal),
    },
    [ThirdPartyRefundProvider.Venmo]: {
      icon: <VenmoIcon height={24} width={24} />,
      label: thirdPartyRefundProviderName(ThirdPartyRefundProvider.Venmo),
    },
    [ThirdPartyRefundProvider.Stripe]: {
      icon: <BankIcon height={24} width={24} />,
      label: t("Bank account (Stripe)"),
    },
  };
  const { icon, label } = providerCopy[provider];

  return (
    <Flex data-ui="refund-method-summary" flexDirection="column" gap="lg">
      <Text as="h2" fontWeight="bold">
        {t("Refund method")}
      </Text>
      <Flex align="center" gap="lg" justify="space-between">
        <Flex align="center" gap="lg">
          {icon}
          <Flex dir="column" gap="none">
            <Text as="p" fontWeight="medium">
              {label}
            </Text>
            <Text as="p" fontSize="sm" textColor="secondary">
              {recipient ||
                (provider === ThirdPartyRefundProvider.Stripe
                  ? t("Connected on Stripe's secure page")
                  : "")}
            </Text>
          </Flex>
        </Flex>
        {canEdit && (
          <RedoButton
            dataTestId="edit-refund-method"
            hierarchy="secondary"
            onClick={onEditClick}
            size="sm"
            text={t("Edit")}
          />
        )}
      </Flex>
      {!canEdit && (
        <Text as="p" fontSize="sm" textColor="secondary">
          {lockedReason(status, t)}
        </Text>
      )}
    </Flex>
  );
});

function lockedReason(
  status: ReturnDisbursementStatus,
  t: (key: string) => string,
): string {
  switch (status) {
    case ReturnDisbursementStatus.Paid:
      return t("This refund has already been sent.");
    case ReturnDisbursementStatus.InFlight:
      return t("This refund is on its way and can no longer be changed.");
    case ReturnDisbursementStatus.Pending:
    case ReturnDisbursementStatus.Failed:
    case ReturnDisbursementStatus.Canceled:
      return t("This refund method can no longer be changed.");
  }
}
